// Initiatives, realizations and business cases on NomOS (SEN-395 / TZ-2, port wave family `initiatives`).
//
// An initiative is an `initiative` record (energy pack; Sentinel's "opportunity" is an initiative in status
// `opportunity`, opportunities as metadata, SEN-390). Reads reshape records/query; counts, sums and distinct
// buildings come from records/query group_by / aggregates (never summed here). Lifecycle actions are the
// initiative_* verbs (Sentinel's state machine, validated server-side, monad/initiatives); realization is the
// initiative_recompute_realization / _lock_realization verbs, which read the query engine. Business cases are
// `business_case` records written by the business_case_* verbs (deterministic NPV/IRR in NomOS). A person's
// action applies at once; the same verbs from an agent go to Approvals (owner ruling).
import { AxiosError } from 'axios';
import type {
  CountryInitiativeCounts,
  InitiativeAggregates,
  InitiativeLifecycleBucket,
  InitiativeLifecycleCounts,
  SavingsInitiative,
  SavingsInitiativeCreate,
} from '@/types';
import {
  invokeVerb, nomosFetch, queryRecords, scalar, toAxiosError, type NomosEnvelope, type NomosFilter,
} from '../client';
import { resolveRecordIds, rememberId } from '../ids';
import type { AnyFn, NomosRegistry } from '../registry';

const TYPE = 'initiative';

/** Sentinel LIFECYCLE_BUCKET_SQL. NomOS keeps no superseded_at: superseded rows were not migrated (owner ruling SEN-387). */
export const BUCKETS: Record<InitiativeLifecycleBucket, string[]> = {
  opportunities: ['opportunity'], active: ['active'], realizations: ['completed', 'verified'], archive: ['cancelled', 'superseded'],
};
const LIVE = ['opportunity', 'planned', 'in_progress', 'active', 'completed', 'verified'];
export const VALID_CREATE_STATUSES = ['opportunity', 'completed'];

const SITE_SID = 'site.sentinel_building_id';
const SITE_COUNTRY = 'site.country_ref.code';
const FIELDS = [
  'name', 'description', 'initiative_type', 'status', 'site', 'implementation_date', 'completion_date', 'roi_date',
  'estimated_annual_savings_kwh', 'estimated_annual_savings_eur', 'estimated_cost_eur', 'actual_savings_kwh',
  'actual_savings_eur', 'actual_cost_eur', 'payback_months', 'impact_percent', 'originated_from', 'energy_type', 'notes',
  'owner_name', 'owner_email', 'source', 'evidence_doc', 'savings_confidence', 'promoted_at', 'realized_at', 'approved_at',
  'approved_by', 'savings_to_date_kwh', 'savings_to_date_eur', 'days_elapsed', 'data_quality_warning',
  'annualised_savings_kwh_interim', 'annualised_savings_eur_interim', 'final_annualised_savings_kwh',
  'final_annualised_savings_eur', 'annualised_savings_locked_at', 'sentinel_created_at', 'sentinel_initiative_id',
  // the site's Sentinel id and country, projected through the reference (no second round trip for them)
  SITE_SID, SITE_COUNTRY,
];
/** The editable fields of Sentinel's full-replace PUT (SavingsInitiativeCreate minus status / owner_user_id). */
const EDITABLE = [
  'name', 'description', 'initiative_type', 'implementation_date', 'completion_date', 'estimated_annual_savings_kwh',
  'estimated_annual_savings_eur', 'estimated_cost_eur', 'actual_savings_kwh', 'actual_savings_eur', 'actual_cost_eur',
  'payback_months', 'impact_percent', 'originated_from', 'energy_type', 'notes', 'owner_name', 'owner_email',
] as const;
const SORTS = [{ field: 'sentinel_created_at', direction: 'desc' as const }, { field: 'name', direction: 'asc' as const }];

// ---------- small reshape helpers ----------
type Cells = Record<string, unknown>;
const str = (x: unknown): string | null => { const s = scalar(x); return typeof s === 'string' && s !== '' ? s : null; };
const num = (x: unknown): number | null => { const s = scalar(x); return typeof s === 'number' && Number.isFinite(s) ? s : null; };
const refId = (x: unknown): string | null => {
  const c = Array.isArray(x) ? x[0] : x;
  return c && typeof c === 'object' && 'target_record_id' in (c as object) ? String((c as { target_record_id: string }).target_record_id) : null;
};
const refName = (x: unknown): string | null => {
  const c = Array.isArray(x) ? x[0] : x;
  return c && typeof c === 'object' ? ((c as { target_kenning?: string }).target_kenning ?? null) : null;
};
/** Postgres `::text` spelling of a timestamptz (Sentinel casts with ::text): `2026-10-07 12:50:04.335795+00`. Reshape only. */
export const pgText = (iso: string | null): string | null => (iso ? iso.replace('T', ' ').replace(/\+00:00$|Z$/, '+00') : null);
const and = (cs: NomosFilter[]): NomosFilter | undefined => (cs.length ? (cs.length === 1 ? cs[0] : { and: cs }) : undefined);
const eq = (attribute: string, value: unknown): NomosFilter => ({ attribute, op: 'eq', value });
const NO_MATCH = '00000000-0000-0000-0000-000000000000';

const notFound = () => toAxiosError(404, { detail: 'Savings initiative not found' }, '');
const isUuid = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

// ---------- ids: Sentinel initiative id <-> record id; site record -> building id / country code ----------
const sidToRid = new Map<string, string>(); const ridToSid = new Map<string, string>();
const siteInfo = new Map<string, { building_id: string; country_code: string | null }>();
let countryCodes: Promise<Map<string, string>> | null = null;
export function clearInitiativeCaches(): void { sidToRid.clear(); ridToSid.clear(); siteInfo.clear(); countryCodes = null; }

async function initiativeRid(id: string): Promise<string> {
  const known = sidToRid.get(id);
  if (known) return known;
  const { rows } = await queryRecords(TYPE, { filter: eq('sentinel_initiative_id', id), limit: 1 });
  let rid = rows[0]?.record_id;
  if (!rid && isUuid(id)) {
    try { await nomosFetch('GET', `/objects/${TYPE}/records/${id}`); rid = id; } catch { /* not a record id either */ }
  }
  if (!rid) throw notFound();
  sidToRid.set(id, rid); ridToSid.set(rid, id);
  return rid;
}
/** The id the screen carries: the Sentinel id of a migrated initiative, else the NomOS record id. */
const outId = (rid: string, sid: string | null) => { const id = sid ?? rid; sidToRid.set(id, rid); ridToSid.set(rid, id); return id; };

async function countries(): Promise<Map<string, string>> {
  if (!countryCodes) {
    countryCodes = queryRecords('country', { fields: ['code'], limit: 500 })
      .then(({ rows }) => new Map(rows.map((r) => [r.record_id, str(r.values.code) ?? ''])));
    countryCodes.catch(() => { countryCodes = null; });
  }
  return countryCodes;
}

async function sites(recordIds: string[]): Promise<Map<string, { building_id: string; country_code: string | null }>> {
  const missing = [...new Set(recordIds)].filter((r) => !siteInfo.has(r));
  if (missing.length) {
    const [res, codes, ids] = await Promise.all([
      nomosFetch<NomosEnvelope<{ values?: Record<string, Cells> }>>('POST', '/objects/site/records/values',
        { record_ids: missing, attributes: ['sentinel_building_id', 'country_ref'] }),
      countries(),
      siteAttrIds(),
    ]);
    for (const r of missing) {
      const cells = res.data?.values?.[r] ?? {};
      const sid = str(cells[ids.sentinel_building_id]) ?? r;
      const country = refId(cells[ids.country_ref]);
      siteInfo.set(r, { building_id: sid, country_code: country ? (codes.get(country) || null) : null });
      rememberId('building', sid, r);
    }
  }
  return siteInfo;
}
let siteAttrs: Promise<Record<string, string>> | null = null;
function siteAttrIds(): Promise<Record<string, string>> {
  if (!siteAttrs) {
    siteAttrs = nomosFetch<NomosEnvelope<Array<{ slug: string; attribute_id: string }>>>('GET', '/objects/site/attributes')
      .then((r) => Object.fromEntries((r.data ?? []).map((a) => [a.slug, a.attribute_id])));
    siteAttrs.catch(() => { siteAttrs = null; });
  }
  return siteAttrs;
}

async function siteRidFor(buildingId: string): Promise<string> {
  const rid = (await resolveRecordIds('building', [buildingId])).get(buildingId);
  if (!rid) throw toAxiosError(404, { detail: `Building ${buildingId} not found. Verify the UUID is correct.` }, '');
  return rid;
}

// ---------- record -> SavingsInitiative ----------
type Row = { record_id: string; kenning: string; values: Cells };
type Item = SavingsInitiative & { country_code?: string | null; buildingId?: string };

export function toInitiative(r: Row, site: { building_id: string; country_code: string | null } | undefined, withCountry: boolean): Item {
  const v = r.values;
  const siteRid = refId(v.site);
  const out: Record<string, unknown> = {
    id: outId(r.record_id, str(v.sentinel_initiative_id)),
    building_id: site?.building_id ?? siteRid,
    building_name: refName(v.site),
    ...(withCountry ? { country_code: site?.country_code ?? null } : {}),
    name: str(v.name) ?? r.kenning,
    description: str(v.description), initiative_type: str(v.initiative_type), status: str(v.status),
    implementation_date: str(v.implementation_date), completion_date: str(v.completion_date), roi_date: str(v.roi_date),
    estimated_annual_savings_kwh: num(v.estimated_annual_savings_kwh), estimated_annual_savings_eur: num(v.estimated_annual_savings_eur),
    estimated_cost_eur: num(v.estimated_cost_eur), actual_savings_kwh: num(v.actual_savings_kwh), actual_savings_eur: num(v.actual_savings_eur),
    actual_cost_eur: num(v.actual_cost_eur), payback_months: num(v.payback_months),
    originated_from: str(v.originated_from), energy_type: str(v.energy_type), notes: str(v.notes),
    owner_name: str(v.owner_name), owner_email: str(v.owner_email), source: str(v.source), evidence_doc: str(v.evidence_doc),
    savings_confidence: str(v.savings_confidence),
    promoted_at: pgText(str(v.promoted_at)), realized_at: pgText(str(v.realized_at)),
    approved_at: pgText(str(v.approved_at)), approved_by: str(v.approved_by),
    savings_to_date_kwh: num(v.savings_to_date_kwh), savings_to_date_eur: num(v.savings_to_date_eur),
    days_elapsed: num(v.days_elapsed), data_quality_warning: str(v.data_quality_warning),
    annualised_savings_kwh_interim: num(v.annualised_savings_kwh_interim), annualised_savings_eur_interim: num(v.annualised_savings_eur_interim),
    final_annualised_savings_kwh: num(v.final_annualised_savings_kwh), final_annualised_savings_eur: num(v.final_annualised_savings_eur),
    annualised_savings_locked_at: pgText(str(v.annualised_savings_locked_at)),
    created_at: pgText(str(v.sentinel_created_at)),
    // NomOS keeps per-cell history, not a row updated_at; no screen reads it (grep frontend/src).
    updated_at: null,
  };
  return out as unknown as Item;
}

/** The leaf of a projected reference path (`{groups: [{value}]}`), or undefined. */
const pathLeaf = (cell: unknown): string | undefined => {
  const v = (cell as { groups?: Array<{ value?: unknown }> } | undefined)?.groups?.[0]?.value;
  const x = scalar(v);
  return typeof x === 'string' ? x : undefined;
};

async function toItems(rows: Row[], withCountry: boolean): Promise<Item[]> {
  for (const r of rows) { // seed the site lookups from the list's own projection
    const rid = refId(r.values.site);
    const sid = pathLeaf(r.values[SITE_SID]);
    if (rid && sid && !siteInfo.has(rid)) {
      siteInfo.set(rid, { building_id: sid, country_code: pathLeaf(r.values[SITE_COUNTRY]) ?? null });
      rememberId('building', sid, rid);
    }
  }
  const info = await sites(rows.map((r) => refId(r.values.site)).filter((x): x is string => !!x));
  return rows.map((r) => toInitiative(r, info.get(refId(r.values.site) ?? ''), withCountry));
}

async function loadOne(rid: string, withCountry = false): Promise<Item> {
  const { rows } = await queryRecordsById(rid);
  if (!rows.length) throw notFound();
  return (await toItems(rows, withCountry))[0];
}
async function queryRecordsById(rid: string): Promise<{ rows: Row[] }> {
  const rec = await nomosFetch<NomosEnvelope<{ kenning: string }>>('GET', `/objects/${TYPE}/records/${rid}`).catch((e) => {
    if ((e as AxiosError).response?.status === 404) throw notFound();
    throw e;
  });
  const [vals, ids] = await Promise.all([
    nomosFetch<NomosEnvelope<{ values?: Record<string, Cells> }>>('POST', `/objects/${TYPE}/records/values`, { record_ids: [rid], attributes: FIELDS }),
    attrIds(TYPE),
  ]);
  const slugOf = Object.fromEntries(Object.entries(ids).map(([s, id]) => [id, s]));
  const cells = vals.data?.values?.[rid] ?? {};
  return { rows: [{ record_id: rid, kenning: rec.data.kenning, values: Object.fromEntries(Object.entries(cells).map(([k, v]) => [slugOf[k] ?? k, v])) }] };
}
const attrCache = new Map<string, Promise<Record<string, string>>>();
function attrIds(type: string): Promise<Record<string, string>> {
  let p = attrCache.get(type);
  if (!p) {
    p = nomosFetch<NomosEnvelope<Array<{ slug: string; attribute_id: string }>>>('GET', `/objects/${type}/attributes`)
      .then((r) => Object.fromEntries((r.data ?? []).map((a) => [a.slug, a.attribute_id])));
    p.catch(() => attrCache.delete(type));
    attrCache.set(type, p);
  }
  return p;
}

/** Every page of a records/query (NomOS caps a page at 1000). */
async function allRows(filter: NomosFilter | undefined): Promise<Row[]> {
  const out: Row[] = [];
  for (let offset = 0; ; offset += 1000) {
    const page = await queryRecords(TYPE, { filter, fields: FIELDS, limit: 1000, cursor: offset, sorts: SORTS });
    out.push(...page.rows);
    if (!page.rows.length || out.length >= page.total) break;
  }
  return out;
}

// ---------- reads ----------
async function getSavingsInitiatives(buildingId: string, params?: { status?: string; initiative_type?: string }): Promise<SavingsInitiative[]> {
  // Sentinel's route takes status_filter / initiative_type; `status` (the api.ts name) never reached it, so it is ignored here too.
  void params?.status;
  const site = await siteRidFor(buildingId);
  const cs: NomosFilter[] = [eq('site', site)];
  if (params?.initiative_type) cs.push(eq('initiative_type', params.initiative_type));
  return toItems(await allRows(and(cs)), false);
}

type BatchParams = {
  building_ids?: string[]; country_code?: string; status?: string; status_filter?: string; initiative_type?: string;
  lifecycle?: InitiativeLifecycleBucket; include_archived?: boolean; page?: number; page_size?: number; by_country?: boolean;
};
type BatchResult = {
  data: (SavingsInitiative & { buildingId?: string; country_code?: string })[]; total: number; buildings_count: number;
  counts: InitiativeLifecycleCounts; aggregates: InitiativeAggregates; counts_by_country: CountryInitiativeCounts[] | null;
};

const statusIn = (values: string[]): NomosFilter => ({ attribute: 'status', op: 'in', value: values });

/** Fold NomOS status counts into Sentinel's four buckets. */
export function bucketCounts(byStatus: Record<string, number>): InitiativeLifecycleCounts {
  const sum = (ss: string[]) => ss.reduce((a, s) => a + (byStatus[s] ?? 0), 0);
  return { opportunities: sum(BUCKETS.opportunities), active: sum(BUCKETS.active), realizations: sum(BUCKETS.realizations), archive: sum(BUCKETS.archive) };
}
/** Sentinel compute_initiative_payback_months: one ratio of the two NomOS sums (0 when savings <= 0). */
export const paybackMonths = (investment: number, savings: number): number => (savings > 0 ? (investment / savings) * 12 : 0);

async function groups(groupBy: string[], filter?: NomosFilter): Promise<Array<{ key: Array<string | null>; count: number }>> {
  const out: Array<{ key: Array<string | null>; count: number }> = [];
  for (let offset = 0; ; offset += 1000) {
    const r = await nomosFetch<NomosEnvelope<Array<{ key: Array<string | null>; count: number }>>>('POST', `/objects/${TYPE}/records/query`,
      { group_by: groupBy, limit: 1000, offset, ...(filter ? { filter } : {}) });
    out.push(...(r.data ?? []));
    if (!(r.data ?? []).length || out.length >= (r.meta?.pagination?.total ?? out.length)) break;
  }
  return out;
}

/** The scope clauses of a list read: sites, country, type (`base`: what the tab counts are taken over) and `filter`
 *  (base plus the lifecycle bucket and status). ONE builder for the screen's KPIs and its table (SEN-439). */
async function batchFilters(params: BatchParams): Promise<{ base: NomosFilter[]; filter: NomosFilter | undefined }> {
  const base: NomosFilter[] = [];
  if (params.building_ids?.length) {
    const map = await resolveRecordIds('building', params.building_ids);
    const rids = params.building_ids.map((b) => map.get(b)).filter((x): x is string => !!x);
    base.push(rids.length ? { attribute: 'site', op: 'in', value: rids } : eq('site', NO_MATCH));
  }
  if (params.country_code) base.push(eq('site.country_ref.code', params.country_code.toUpperCase()));
  if (params.initiative_type) base.push(eq('initiative_type', params.initiative_type));
  const scoped = [...base];
  if (params.lifecycle) scoped.push(statusIn(BUCKETS[params.lifecycle]));
  else if (!params.include_archived) scoped.push(statusIn(LIVE));
  if (params.status_filter) scoped.push(eq('status', params.status_filter));
  return { base, filter: and(scoped) };
}

/** What the Initiatives screen's own controls narrow the list to, beyond the batch read's scope. */
export type InitiativeListScope = BatchParams & {
  building_type?: string; payback_from?: number; payback_to?: number; drilled_building_id?: string; drilled_country?: string;
};

/** The screen's filters as ONE contract filter for the Poiesis table, so its rows are the rows the KPIs and the
 *  charts count (the same bucket, sites, country, status and type; the building type and payback window too). */
export async function initiativeListFilter(p: InitiativeListScope): Promise<NomosFilter | undefined> {
  const { filter } = await batchFilters(p);
  const extra: NomosFilter[] = [];
  if (p.drilled_building_id) {
    const rid = (await resolveRecordIds('building', [p.drilled_building_id])).get(p.drilled_building_id);
    extra.push(rid ? eq('site', rid) : eq('site', NO_MATCH));
  } else if (p.drilled_country) extra.push(eq('site.country_ref.code', p.drilled_country.toUpperCase()));
  if (p.building_type) extra.push(eq('site.building_type', p.building_type.toLowerCase()));
  if (p.payback_from != null) extra.push({ attribute: 'payback_months', op: 'gte', value: p.payback_from });
  if (p.payback_to != null) extra.push({ attribute: 'payback_months', op: 'lte', value: p.payback_to });
  return and([...(filter ? [filter] : []), ...extra]);
}

/** The id the screen carries for a record (its Sentinel id when it has one), as the batch read names it. */
export const initiativeScreenId = (recordId: string): string => ridToSid.get(recordId) ?? recordId;

async function getBatchInitiatives(params: BatchParams = {}): Promise<BatchResult> {
  const page = params.page ?? 1; const size = params.page_size ?? 500;
  const { base, filter } = await batchFilters(params);

  const [summary, distinctSites, byStatus, byCountry, pageRows] = await Promise.all([
    nomosFetch<NomosEnvelope<Array<{ count: number; aggregates?: Array<{ attribute: string; value: number | null }> }>>>(
      'POST', `/objects/${TYPE}/records/query`, {
        aggregates: [{ fn: 'sum', attribute: 'estimated_annual_savings_eur' }, { fn: 'sum', attribute: 'estimated_cost_eur' }],
        limit: 1, ...(filter ? { filter } : {}) }),
    nomosFetch<NomosEnvelope<unknown[]>>('POST', `/objects/${TYPE}/records/query`, { group_by: ['site'], limit: 1, ...(filter ? { filter } : {}) }),
    groups(['status'], and(base)),
    params.by_country ? groups(['site.country_ref.code', 'status'], and(base)) : Promise.resolve(null),
    queryRecords(TYPE, { filter, fields: FIELDS, limit: size, cursor: (page - 1) * size, sorts: SORTS }),
  ]);
  const agg = Object.fromEntries((summary.data?.[0]?.aggregates ?? []).map((a) => [a.attribute, a.value ?? 0]));
  const total = summary.data?.[0]?.count ?? pageRows.total;
  const buildings = distinctSites.meta?.pagination?.total ?? 0;
  const savings = agg.estimated_annual_savings_eur ?? 0; const investment = agg.estimated_cost_eur ?? 0;
  let countsByCountry: CountryInitiativeCounts[] | null = null;
  if (byCountry) {
    const per = new Map<string | null, Record<string, number>>();
    for (const g of byCountry) {
      const m = per.get(g.key[0]) ?? {}; m[String(g.key[1])] = (m[String(g.key[1])] ?? 0) + g.count; per.set(g.key[0], m);
    }
    countsByCountry = [...per.entries()]
      .sort(([a], [b]) => (a === null ? 1 : b === null ? -1 : a.localeCompare(b)))
      .map(([country_code, m]) => ({ country_code, ...bucketCounts(m) }));
  }
  const data = (await toItems(pageRows.rows, true)).map((d) => ({ ...d, buildingId: d.building_id }));
  return {
    data: data as BatchResult['data'], total, buildings_count: buildings,
    counts: bucketCounts(Object.fromEntries(byStatus.map((g) => [String(g.key[0]), g.count]))),
    aggregates: { total_savings_eur: savings, total_investment_eur: investment, payback_months: paybackMonths(investment, savings), building_count: buildings },
    counts_by_country: countsByCountry,
  };
}

// ---------- errors: NomOS refusal codes -> Sentinel's status + detail ----------
type Refusal = { code?: string; details?: Record<string, string> };
const refusalOf = (e: unknown): Refusal | null => {
  const data = (e as AxiosError<{ error?: Refusal }>).response?.data;
  return data?.error ?? null;
};
const sorted = (xs: string[]) => `[${xs.map((x) => `'${x}'`).join(', ')}]`;
const TRANSITIONS: Record<string, string[]> = {
  opportunity: ['active', 'cancelled'], active: ['completed', 'opportunity', 'cancelled'], completed: ['active', 'cancelled', 'verified'],
  verified: ['active'], cancelled: ['opportunity'], superseded: [],
};

/** Map a verb refusal to the error Sentinel's endpoint answers. `kind` picks Sentinel's wording per endpoint. */
export function sentinelError(e: unknown, kind: 'transition' | 'update' | 'lock' | 'recompute' = 'transition'): unknown {
  const r = refusalOf(e);
  if (!r?.code) {
    const status = (e as AxiosError).response?.status;
    return status === 404 ? notFound() : e;
  }
  const d = r.details ?? {};
  switch (r.code) {
    case 'initiative_invalid_transition':
      if (kind === 'update') {
        const allowed = [...(TRANSITIONS[d.from_status] ?? [])].sort();
        return toAxiosError(400, { detail: `Cannot transition from '${d.from_status}' to '${d.to_status}'. Allowed transitions from '${d.from_status}': ${allowed.length ? sorted(allowed) : 'none (terminal state)'}.` }, '');
      }
      return toAxiosError(400, { detail: `Invalid transition: ${d.from_status} → ${d.to_status}` }, '');
    case 'initiative_not_active':
      return toAxiosError(400, { detail: `Cannot complete initiative with status '${d.status}'. Only active initiatives can be completed.` }, '');
    case 'initiative_not_realized':
      return toAxiosError(400, { detail: `Cannot ${kind === 'lock' ? 'lock' : 'recompute'} realization for initiative with status '${d.status}'. Required: completed or verified.` }, '');
    case 'initiative_realization_locked':
      return toAxiosError(409, { detail: 'Realization is already locked; values are immutable.' }, '');
    case 'initiative_realization_no_data':
      return toAxiosError(422, { detail: 'No consumption / baseline data available to compute realization; cannot lock. Verify meter readings exist for the completion window.' }, '');
    case 'record_not_found': case 'not_found':
      return notFound();
    default:
      return e;
  }
}

async function apply(verb: string, parameters: Record<string, unknown>, kind: Parameters<typeof sentinelError>[1] = 'transition'): Promise<void> {
  let res: NomosEnvelope<unknown>;
  try { res = await invokeVerb(verb, parameters, 'apply'); } catch (e) { throw sentinelError(e, kind); }
  const status = (res.data as { status?: string } | undefined)?.status;
  if (status && status !== 'auto_executed') throw toAxiosError(409, { detail: `${verb} was not applied (${status})` }, '');
}

// ---------- writes ----------
function editableValues(data: Partial<SavingsInitiativeCreate>, fillMissing: boolean): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of EDITABLE) {
    const v = (data as Record<string, unknown>)[k];
    if (v !== undefined) out[k] = v;
    else if (fillMissing) out[k] = null; // Sentinel's PUT replaces every field: an omitted one is written as null
  }
  return out;
}

async function createSavingsInitiative(buildingId: string, data: SavingsInitiativeCreate): Promise<SavingsInitiative> {
  const status = data.status ?? 'opportunity';
  // Mid-funnel create (status 'active'): NomOS only creates in VALID_CREATE_STATUSES, so the one user action is
  // "create as opportunity, then walk the opportunity -> active edge through the initiative verbs". If the walk is
  // refused the fresh opportunity is removed again, so a failed save leaves nothing behind.
  const midFunnel = status === 'active';
  if (!midFunnel && !VALID_CREATE_STATUSES.includes(status)) {
    throw toAxiosError(400, { detail: `Cannot create an initiative with status '${status}'. Valid initial statuses: ${sorted([...VALID_CREATE_STATUSES, 'active'])}.` }, '');
  }
  const site = await siteRidFor(buildingId);
  const values = { ...editableValues(data, false), status: midFunnel ? 'opportunity' : status, site, sentinel_created_at: new Date().toISOString() };
  const res = await nomosFetch<NomosEnvelope<{ record_id: string }>>('POST', `/objects/${TYPE}/records`, { values });
  const rid = res.data.record_id;
  if (midFunnel) {
    try {
      const { owner_name, owner_email, implementation_date } = data;
      if (owner_name && owner_email && implementation_date) {
        await apply('initiative_activate', { record_id: rid, owner_name, owner_email, implementation_date: String(implementation_date).slice(0, 10) });
      } else {
        await apply('initiative_change_status', { record_id: rid, target_status: 'active' });
      }
    } catch (e) {
      await nomosFetch('DELETE', `/objects/${TYPE}/records/${rid}`).catch(() => undefined);
      throw e;
    }
  }
  return loadOne(rid);
}

async function updateSavingsInitiative(initiativeId: string, data: Partial<SavingsInitiativeCreate>): Promise<SavingsInitiative> {
  const rid = await initiativeRid(initiativeId);
  const target = data.status ?? 'opportunity'; // pydantic default of the replaced row
  const current = (await loadOne(rid)).status;
  if (current !== target) await apply('initiative_change_status', { record_id: rid, target_status: target }, 'update');
  await nomosFetch('PATCH', `/objects/${TYPE}/records/${rid}`, { values: editableValues(data, true) });
  return loadOne(rid);
}

async function deleteSavingsInitiative(initiativeId: string): Promise<void> {
  const rid = await initiativeRid(initiativeId).catch(() => { throw toAxiosError(404, { detail: 'Initiative not found' }, ''); });
  await nomosFetch('DELETE', `/objects/${TYPE}/records/${rid}`);
}

async function completeInitiative(id: string, data: {
  completion_date?: string; actual_savings_kwh?: number; actual_savings_eur?: number; actual_cost_eur?: number; notes?: string;
}): Promise<SavingsInitiative> {
  const rid = await initiativeRid(id);
  await apply('initiative_complete', { record_id: rid, ...dropUndefined(data) });
  return loadOne(rid);
}
async function activateInitiative(id: string, data: { owner_name: string; owner_email: string; implementation_date: string }): Promise<SavingsInitiative> {
  const rid = await initiativeRid(id);
  await apply('initiative_activate', { record_id: rid, ...data });
  return loadOne(rid);
}
async function changeInitiativeStatus(id: string, data: { target_status: string; reason?: string }): Promise<SavingsInitiative> {
  const rid = await initiativeRid(id);
  await apply('initiative_change_status', { record_id: rid, ...dropUndefined(data) });
  return loadOne(rid);
}
async function cancelInitiative(id: string, data: { reason?: string }): Promise<SavingsInitiative> {
  const rid = await initiativeRid(id);
  await apply('initiative_cancel', { record_id: rid, ...dropUndefined(data ?? {}) });
  return loadOne(rid);
}
const dropUndefined = <T extends object>(o: T): Partial<T> => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null)) as Partial<T>;

/** Sentinel LockRealizationResponse (the wire shape, whatever api.ts's annotation says). */
async function lockRealization(id: string): Promise<SavingsInitiative> {
  const rid = await initiativeRid(id);
  await apply('initiative_lock_realization', { record_id: rid }, 'lock');
  const it = await loadOne(rid);
  return {
    id: it.id, annualised_savings_kwh: it.final_annualised_savings_kwh ?? 0, annualised_savings_eur: it.final_annualised_savings_eur ?? null,
    annualised_savings_locked_at: it.annualised_savings_locked_at,
  } as unknown as SavingsInitiative;
}

/** Sentinel RealizationResponse (realizations._row_to_response). */
async function recomputeRealization(id: string): Promise<SavingsInitiative> {
  const rid = await initiativeRid(id);
  await apply('initiative_recompute_realization', { record_id: rid }, 'recompute');
  const it = await loadOne(rid);
  return {
    id: it.id, building_id: it.building_id, customer_id: null, name: it.name, initiative_type: it.initiative_type, status: it.status,
    completion_date: it.completion_date ?? null, realization_status: it.annualised_savings_locked_at ? 'locked' : 'interim',
    annualised_savings_kwh_interim: it.annualised_savings_kwh_interim ?? null, annualised_savings_eur_interim: it.annualised_savings_eur_interim ?? null,
    final_annualised_savings_kwh: it.final_annualised_savings_kwh ?? null, final_annualised_savings_eur: it.final_annualised_savings_eur ?? null,
    annualised_savings_locked_at: it.annualised_savings_locked_at ?? null,
    estimated_annual_savings_kwh: it.estimated_annual_savings_kwh ?? null, estimated_annual_savings_eur: it.estimated_annual_savings_eur ?? null,
    savings_to_date_kwh: it.savings_to_date_kwh ?? null, savings_to_date_eur: it.savings_to_date_eur ?? null,
    days_elapsed: it.days_elapsed ?? null, data_quality_warning: it.data_quality_warning ?? null, updated_at: null,
  } as unknown as SavingsInitiative;
}

async function createSolarBusinessCase(): Promise<never> {
  // Sentinel has no POST /solar/business-cases route (its endpoint is /buildings/{id}/solar-cases) and no screen calls this method.
  throw toAxiosError(404, { detail: 'Not Found' }, '');
}

export const initiativesImpl: NomosRegistry = {
  getSavingsInitiatives: getSavingsInitiatives as unknown as AnyFn,
  getBatchInitiatives: getBatchInitiatives as unknown as AnyFn,
  createSavingsInitiative: createSavingsInitiative as unknown as AnyFn,
  updateSavingsInitiative: updateSavingsInitiative as unknown as AnyFn,
  deleteSavingsInitiative: deleteSavingsInitiative as unknown as AnyFn,
  completeInitiative: completeInitiative as unknown as AnyFn,
  activateInitiative: activateInitiative as unknown as AnyFn,
  changeInitiativeStatus: changeInitiativeStatus as unknown as AnyFn,
  lockRealization: lockRealization as unknown as AnyFn,
  recomputeRealization: recomputeRealization as unknown as AnyFn,
  cancelInitiative: cancelInitiative as unknown as AnyFn,
  createSolarBusinessCase: createSolarBusinessCase as unknown as AnyFn,
};
